import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Layers, ListTree, Info } from "lucide-react";
import { cleanBrandName, formatMoney } from "@/lib/utils";
import { formatBomNorm } from "@/shared/helpers/product-costing.helper";

interface ProductSyncResultModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: any;
  productUnit?: string;
  onViewBom?: () => void;
}

export function ProductSyncResultModal({
  open,
  onOpenChange,
  result,
  productUnit = "SP",
  onViewBom,
}: ProductSyncResultModalProps) {
  if (!result) return null;

  const bomChanges: any[] = Array.isArray(result.bomChanges)
    ? result.bomChanges
    : [];
  const routingChanges: any[] = Array.isArray(result.routingChanges)
    ? result.routingChanges
    : [];

  const basisExplanation: string =
    result.calculationBasis?.explanation ||
    result.calculationBasis?.summaryText ||
    "";

  const getActionBadge = (action: string) => {
    if (action === "CREATED" || action === "ADDED") {
      return (
        <Badge
          variant="outline"
          className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[11px]"
        >
          Mới thêm
        </Badge>
      );
    }
    if (action === "UPDATED") {
      return (
        <Badge
          variant="outline"
          className="bg-amber-50 text-amber-800 border-amber-200 text-[11px]"
        >
          Đã cập nhật
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="bg-slate-50 text-slate-600 border-slate-200 text-[11px]"
      >
        Giữ nguyên
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="product-sync-result-modal"
        className="sm:max-w-[700px] max-h-[88vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Kết quả Đồng bộ Định mức Vật tư (BOM) & Quy trình (Routing)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1 text-xs">
          {/* 1. Cơ sở tính & Giá vốn sau đồng bộ (đơn giá niêm yết do kinh doanh nhập, đồng bộ không đổi) */}
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2.5">
            <div className="flex items-start gap-2 text-slate-700">
              <Info className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
              <div className="space-y-0.5 min-w-0">
                <p className="leading-snug font-medium text-slate-800">
                  {result.message ||
                    basisExplanation ||
                    "Đã đối soát và chuẩn hóa định mức sản xuất."}
                </p>
                {basisExplanation && result.message && (
                  <p className="text-[11px] text-slate-600 leading-snug">
                    {basisExplanation}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-200/80">
              <div className="flex items-center justify-between bg-white px-3 py-2 rounded border border-slate-200/80">
                <span className="text-slate-600">
                  Giá vốn chuẩn / 1 {productUnit}:
                </span>
                <span className="font-mono font-semibold text-emerald-700">
                  {formatMoney(result.previousCostPrice ?? 0)} đ →{" "}
                  {formatMoney(result.unitCost ?? 0)} đ
                </span>
              </div>
              <div className="flex items-center justify-between bg-white px-3 py-2 rounded border border-slate-200/80">
                <span className="text-slate-600">
                  Đơn giá niêm yết (Catalog):
                </span>
                <span
                  className="font-mono font-semibold text-indigo-700 text-right"
                  data-testid="sync-catalog-price-unchanged"
                >
                  {Number(result.previousCatalogPrice ?? 0) > 0
                    ? `${formatMoney(result.previousCatalogPrice ?? 0)} đ`
                    : "Chưa nhập"}
                  <span className="block font-sans font-normal text-[10px] text-slate-500">
                    Giữ nguyên, do kinh doanh nhập
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* 2. Chi tiết Định mức Vật tư (BOM) */}
          <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
            <div className="px-3.5 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-1.5 font-semibold text-slate-800">
                <Layers className="h-4 w-4 text-blue-600" />
                <span>1. Định mức Vật tư (BOM)</span>
              </div>
              <span className="text-[11px] text-slate-500">
                {bomChanges.length} dòng vật tư
              </span>
            </div>

            {bomChanges.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {bomChanges.map((item: any, idx: number) => {
                  const prevQty = item.previousQty ?? item.previousQuantity;
                  const nextQty = item.newQty ?? item.newQuantity ?? 0;
                  const isNewlyCreated =
                    item.action === "CREATED" || item.action === "ADDED";
                  const prevLabel =
                    isNewlyCreated || prevQty === null || prevQty === undefined
                      ? "Chưa có"
                      : `${formatBomNorm(prevQty)} ${item.unit || ""}`.trim();
                  const detailNote =
                    item.note || item.formulaExplanation || item.reason || "";

                  return (
                    <div
                      key={idx}
                      className="px-3.5 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                    >
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800">
                            {cleanBrandName(item.materialName)}
                          </span>
                          {getActionBadge(item.action)}
                        </div>
                        {detailNote && (
                          <p className="text-[11px] text-slate-500 leading-snug">
                            {detailNote}
                          </p>
                        )}
                      </div>
                      <div className="font-mono text-right shrink-0 bg-slate-50 px-2.5 py-1 rounded border border-slate-200/80">
                        <span className="text-slate-500">{prevLabel}</span>
                        <span className="mx-1.5 text-slate-400">→</span>
                        <span className="font-bold text-emerald-700">
                          {formatBomNorm(nextQty)} {item.unit || ""}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center text-slate-500">
                Không có dòng định mức vật tư nào thay đổi.
              </div>
            )}
          </div>

          {/* 3. Chi tiết Quy trình Sản xuất (Routing) */}
          <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
            <div className="px-3.5 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-1.5 font-semibold text-slate-800">
                <ListTree className="h-4 w-4 text-indigo-600" />
                <span>2. Quy trình Sản xuất (Routing)</span>
              </div>
              <span className="text-[11px] text-slate-500">
                {routingChanges.length} công đoạn
              </span>
            </div>

            {routingChanges.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {routingChanges.map((step: any, idx: number) => {
                  const prevNorm = step.previousNormSummary || "";
                  const nextNorm = step.newNormSummary || step.detail || "";
                  const showDiff =
                    (step.action === "CREATED" ||
                      step.action === "ADDED" ||
                      step.action === "UPDATED") &&
                    prevNorm &&
                    nextNorm &&
                    prevNorm !== nextNorm;

                  return (
                    <div
                      key={idx}
                      className="px-3.5 py-2.5 flex flex-col gap-1"
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800">
                            {idx + 1}. {step.processName}
                          </span>
                          {getActionBadge(step.action)}
                        </div>
                        {nextNorm && (
                          <span className="text-[11px] text-slate-700 font-mono">
                            {showDiff ? (
                              <>
                                <span className="text-slate-400">
                                  {prevNorm}
                                </span>
                                <span className="mx-1.5 text-slate-400">→</span>
                                <span className="font-semibold text-indigo-700">
                                  {nextNorm}
                                </span>
                              </>
                            ) : (
                              nextNorm
                            )}
                          </span>
                        )}
                      </div>
                      {step.formulaExplanation && (
                        <p className="text-[11px] text-slate-500 leading-snug">
                          {step.formulaExplanation}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center text-slate-500">
                Quy trình sản xuất giữ nguyên.
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Đóng
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            data-testid="view-synced-bom-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={() => {
              onOpenChange(false);
              onViewBom?.();
            }}
          >
            Xem Định mức Vật tư
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
